import { AxiosInstance, AxiosResponse } from 'axios';
import {
    TPhoneVerificationPUT,
    TPhoneVerficationPOST,
    TPhoneVerificationResponse,
    TPhonesResponse,
    TPhonesCallbackResponse,
    TProfile,
    TProfilePUT,
    TRedeemResponse,
    TReturnsPOSTResponse,
    TReturnsPOST,
    TPaymentCardsResponse,
    TCredit,
    TReturns,
    TReturnables,
    TInitTokens,
    TTokensPOST,
    TTokensPOSTResponse,
    TLoginPOST,
    TLoginPOSTResponse,
    TSignUpPOST,
    TSignUpPOSTResponse,
    TResetPOST,
    TResetPOSTResponse,
    THomatPOST,
    TPasswordPUT,
    THomat,
    TClaimsGETResponse,
    TClaimsPOST,
    TClaimablesGETResponse,
    TSubmitOTPPayload,
    TRequestOTPPayload,
    TRequestOTPResponse,
    TSubmitOTPResponse,
    TBankTransfer,
    TTransfersResponse,
    TBankAccount,
    TBankAccountReponse,
    TGetTransferData,
    TGetLoyaltyResponse,
    TBankTransferOTPPayload,
    TRequestBankTransferOTPResponse,
    TBankTransferOTPSubmitPayload,
    TBankTransferOTPSubmitResponse,
    TPreferencesGETResponse,
    TPreferencePOSTRequest,
    TReturnsTrackingResponse,
    TReturnChangeAddressPOSTResponse,
    TReturnChangeAddressPOST,
    TClaimChangeAddressPOST,
} from '../types/customer';
import {
    TLocation,
    TAddressesGET,
    TAddressChangePUTResponse,
    TAddressesPOST,
    TAddressesPOSTResponse,
    TCollectionPointResponse,
    TDailyPropertyListGET,
} from '../types/customer_address';
import { TLocaleAPI } from '../types/locale';
import {
    TOrders,
    TOrderDetail,
    TCancellable,
    TCancelPOST,
    TCancelResponse,
    TReviewableList,
    IPurchaseReviewGet,
    IProductReviewGet,
    IProductRatingCreate,
    IPurchaseRatingCreate,
} from '../types/orders';
import getFingerprint from '../utils/fingerprint';
import { reportEvent } from '../utils/sentry';

// ----------------------------------------- ADDRESS -----------------------------------------
type TAddressesEndpoints = {
    get: () => Promise<AxiosResponse<TAddressesGET>>;
    getAreaFromLocation: (lat: number, lng: number) => Promise<AxiosResponse<TLocation>>;
    getCollectionPoints: () => Promise<AxiosResponse<TCollectionPointResponse>>;
    getDailyAreaBrowseFromLocation: (lat: number, lng: number) => Promise<AxiosResponse<TLocation>>;
    getDailyAreaFromLocation: (lat: number, lng: number) => Promise<AxiosResponse<TLocation>>;
    getDailyProperties: (propertiesData: {
        lat: number;
        lng: number;
        property_type: string;
    }) => Promise<AxiosResponse<TDailyPropertyListGET>>;
    checkDailyAreaIsSet: () => Promise<AxiosResponse<TLocation>>;
    setDefault: (addressId: string) => Promise<AxiosResponse<TAddressesGET>>;
    post: (address: TAddressesPOST) => Promise<AxiosResponse<TAddressesPOSTResponse>>;
    put: (orderNr: string, idAddress: TAddressesPOST) => Promise<AxiosResponse<TAddressesGET>>;
    remove: (addressId: string) => Promise<AxiosResponse<TAddressesGET>>;
    updateOrderAddress: (orderNr: string, addressId: number) => Promise<AxiosResponse<TAddressChangePUTResponse>>;
};

const address = (api: AxiosInstance, apiLocale: TLocaleAPI): TAddressesEndpoints => {
    const get = async (): Promise<AxiosResponse<TAddressesGET>> =>
        api.get<TAddressesGET>(`/_svc/customer-v1/customer/addresses`, { headers: { 'X-Locale': apiLocale } });

    const getAreaFromLocation = async (lat: number, lng: number): Promise<AxiosResponse<TLocation>> =>
        api.post<TLocation>(
            `/_svc/geo-v1/area`,
            {
                lat,
                lng,
            },
            { headers: { 'X-Locale': apiLocale } },
        );

    const getCollectionPoints = async (): Promise<AxiosResponse<TCollectionPointResponse>> =>
        api.get<TCollectionPointResponse>(`/_svc/geo-v1/collection-point`, { headers: { 'X-Locale': apiLocale } });

    const getDailyAreaBrowseFromLocation = async (lat: number, lng: number): Promise<AxiosResponse<TLocation>> =>
        api.post<TLocation>(
            `/_svc/geo-v1/area-daily-browse`,
            {
                lat,
                lng,
            },
            { headers: { 'X-Locale': apiLocale } },
        );

    const getDailyAreaFromLocation = async (lat: number, lng: number): Promise<AxiosResponse<TLocation>> =>
        api.post<TLocation>(
            `/_svc/geo-v1/area-daily`,
            {
                lat,
                lng,
            },
            { headers: { 'X-Locale': apiLocale } },
        );

    const checkDailyAreaIsSet = async (): Promise<AxiosResponse<TLocation>> =>
        api.post<TLocation>(`/_svc/geo-v1/area-daily-getset`, {}, { headers: { 'X-Locale': apiLocale } });

    const getDailyProperties = async (propertiesData: {
        lat: number;
        lng: number;
        property_type: 'building' | 'community';
    }): Promise<AxiosResponse<TDailyPropertyListGET>> =>
        api.post<TDailyPropertyListGET>(`/_svc/geo-v1/property-list`, propertiesData, {
            headers: { 'X-Locale': apiLocale },
        });

    const setDefault = async (addressId: string): Promise<AxiosResponse<TAddressesGET>> =>
        api.post<TAddressesGET>(
            `/_svc/customer-v1/customer/addresses/${addressId}/default`,
            {},
            {
                headers: { 'X-Locale': apiLocale },
            },
        );

    const post = async (addressToPost: TAddressesPOST): Promise<AxiosResponse<TAddressesPOSTResponse>> =>
        api.post<TAddressesPOSTResponse>(`/_svc/customer-v1/customer/addresses`, addressToPost, {
            headers: { 'X-Locale': apiLocale },
        });

    const put = async (addressId: string, addressToPut: TAddressesPOST): Promise<AxiosResponse<TAddressesGET>> =>
        api.put<TAddressesGET>(`/_svc/customer-v1/customer/addresses/${addressId}`, addressToPut, {
            headers: { 'X-Locale': apiLocale },
        });

    const remove = async (addressId: string): Promise<AxiosResponse<TAddressesGET>> =>
        api.delete<TAddressesGET>(`/_svc/customer-v1/customer/addresses/${addressId}`, {
            headers: { 'X-Locale': apiLocale },
        });

    const updateOrderAddress = async (
        orderNr: string,
        idAddress: number,
    ): Promise<AxiosResponse<TAddressChangePUTResponse>> =>
        api.put<TAddressChangePUTResponse>(
            `/_svc/customer-v1/customer/orders/${orderNr}`,
            { idAddress },
            { headers: { 'X-Locale': apiLocale } },
        );

    return {
        get,
        getAreaFromLocation,
        getCollectionPoints,
        getDailyAreaBrowseFromLocation,
        getDailyAreaFromLocation,
        checkDailyAreaIsSet,
        getDailyProperties,
        setDefault,
        post,
        put,
        remove,
        updateOrderAddress,
    };
};

// ----------------------------------------- ORDERS -----------------------------------------
type TOrdersEndpoints = {
    list: (page: number, year: number) => Promise<AxiosResponse<TOrders>>;
    get: (orderNr: string) => Promise<AxiosResponse<TOrderDetail>>;
    getCancellable: (orderNr: string) => Promise<AxiosResponse<TCancellable>>;
    cancel: (items: TCancelPOST['items']) => Promise<AxiosResponse<TCancelResponse>>;
    listReviewable: (orderNr: string) => Promise<AxiosResponse<TReviewableList>>;
    getPurchaseReview: (orderNr: string) => Promise<AxiosResponse<IPurchaseReviewGet>>;
    getProductReview: (orderNr: string) => Promise<AxiosResponse<IProductReviewGet>>;
    createPurchaseReview: (review: IPurchaseRatingCreate) => Promise<AxiosResponse<IPurchaseReviewGet>>;
    createProductReview: (review: IProductRatingCreate) => Promise<AxiosResponse<IPurchaseReviewGet>>;
};

const order = (api: AxiosInstance, apiLocale: TLocaleAPI): TOrdersEndpoints => {
    // --- Orders
    const list = async (page = 1, year = 0): Promise<AxiosResponse<TOrders>> => {
        return api.get<TOrders>(`/_svc/order/api/orders?year=${year}&page=${page}`, {
            headers: { 'X-Locale': apiLocale },
        });
    };

    const get = async (orderNr: string): Promise<AxiosResponse<TOrderDetail>> => {
        return api.get<TOrderDetail>(`/_svc/order/api/order/${orderNr}`, { headers: { 'X-Locale': apiLocale } });
    };

    // --- Cancellations
    const getCancellable = async (orderNr: string): Promise<AxiosResponse<TCancellable>> => {
        return api.get<TCancellable>(`/_svc/order/order_items/cancellable/${orderNr}`, {
            headers: { 'X-Locale': apiLocale },
        });
    };

    const cancel = async (items: TCancelPOST['items']): Promise<AxiosResponse<TCancelResponse>> => {
        return api.post<TCancelResponse>(
            `/_svc/order/order_items/cancel`,
            { items },
            { headers: { 'X-Locale': apiLocale } },
        );
    };

    // --- Reviews
    const listReviewable = async (orderNr: string): Promise<AxiosResponse<TReviewableList>> => {
        return api.post<TReviewableList>(
            `/_svc/reviews/collect/v1/purchase/list`,
            { orderNr },
            { headers: { 'X-Locale': apiLocale } },
        );
    };

    const getPurchaseReview = async (purchaseItemNr: string): Promise<AxiosResponse<IPurchaseReviewGet>> => {
        return api.post<IPurchaseReviewGet>(
            `/_svc/reviews/collect/v1/purchase-reviews/get`,
            {
                purchaseItemNr,
            },
            { headers: { 'X-Locale': apiLocale } },
        );
    };

    const getProductReview = async (sku: string): Promise<AxiosResponse<IProductReviewGet>> => {
        return api.post<IProductReviewGet>(
            `/_svc/reviews/collect/v1/product-reviews/get`,
            { sku },
            { headers: { 'X-Locale': apiLocale } },
        );
    };

    const createPurchaseReview = async (review: IPurchaseRatingCreate): Promise<AxiosResponse<IPurchaseReviewGet>> => {
        return api.post<IPurchaseReviewGet>(`/_svc/reviews/collect/v1/purchase-reviews/create`, review, {
            headers: { 'X-Locale': apiLocale },
        });
    };

    const createProductReview = async (review: IProductRatingCreate): Promise<AxiosResponse<IProductReviewGet>> => {
        return api.post<IProductReviewGet>(`/_svc/reviews/collect/v1/product-reviews/create`, review, {
            headers: { 'X-Locale': apiLocale },
        });
    };

    return {
        list,
        cancel,
        get,
        getCancellable,
        listReviewable,
        getProductReview,
        getPurchaseReview,
        createPurchaseReview,
        createProductReview,
    };
};

// ----------------------------------------- USER -----------------------------------------
type TUserAuthenticationEndpoints = {
    postLogin: (payload: TLoginPOST) => Promise<AxiosResponse<TLoginPOSTResponse>>;
    postSignUp: (payload: TSignUpPOST) => Promise<AxiosResponse<TSignUpPOSTResponse>>;
    postLogout: () => Promise<AxiosResponse<void>>;
    postReset: (payload: TResetPOST) => Promise<AxiosResponse<TResetPOSTResponse>>;
};

const user = (api: AxiosInstance, apiLocale: TLocaleAPI): TUserAuthenticationEndpoints => {
    const postLogin = async (payload: TLoginPOST): Promise<AxiosResponse<TLoginPOSTResponse>> =>
        api.post<TLoginPOSTResponse>('/_svc/customer-v1/auth/signin', payload, { headers: { 'X-Locale': apiLocale } });

    const postSignUp = async (payload: TSignUpPOST): Promise<AxiosResponse<TSignUpPOSTResponse>> =>
        api.post<TSignUpPOSTResponse>('/_svc/customer-v1/customer', payload, { headers: { 'X-Locale': apiLocale } });

    const postReset = async (payload: TResetPOST): Promise<AxiosResponse<TResetPOSTResponse>> =>
        api.post<TResetPOSTResponse>('/_svc/customer-v1/auth/reset_password', payload, {
            headers: { 'X-Locale': apiLocale },
        });

    const postLogout = async (): Promise<AxiosResponse<void>> =>
        api.post('/_svc/customer-v1/auth/signout', {}, { headers: { 'X-Locale': apiLocale } });

    return {
        postLogin,
        postSignUp,
        postReset,
        postLogout,
    };
};

// ----------------------------------------- HOMAT -----------------------------------------
type THomatEndpoints = {
    postHomat: (payload: THomatPOST) => Promise<AxiosResponse<THomat>>;
};

const homat = (api: AxiosInstance, apiLocale: TLocaleAPI): THomatEndpoints => {
    const postHomat = async (payload: THomatPOST): Promise<AxiosResponse<THomat>> => {
        const postData = new FormData();
        postData.append('card', payload.card);
        postData.append('cardExpiry', payload.cardExpiry);
        postData.append('idFront', payload.idFront, 'idFront');
        return api.post<THomat>('/_svc/customer-v1/homat', postData, { headers: { 'X-Locale': apiLocale } });
    };
    return {
        postHomat,
    };
};

// ----------------------------------------- PHONE -----------------------------------------
// TODO: otp endpoints are repeated in OTP section below, better remove it
type TPhoneEndpoints = {
    post: (payload: TPhoneVerficationPOST) => Promise<AxiosResponse<TPhoneVerificationResponse>>;
    put: (payload: TPhoneVerificationPUT) => Promise<AxiosResponse<TPhoneVerificationResponse>>;
    get: () => Promise<AxiosResponse<TPhonesResponse>>;
    requestCallback: (phone: string) => Promise<AxiosResponse<TPhonesCallbackResponse>>;
};

const phone = (api: AxiosInstance, apiLocale: TLocaleAPI): TPhoneEndpoints => {
    const get = async (): Promise<AxiosResponse<TPhonesResponse>> =>
        api.get<TPhonesResponse>('/_svc/customer-v1/customer/phones', { headers: { 'X-Locale': apiLocale } });
    const post = async (payload: TPhoneVerficationPOST): Promise<AxiosResponse<TPhoneVerificationResponse>> =>
        api.post<TPhoneVerificationResponse>('/_svc/customer-v1/customer/phone/verify', payload, {
            headers: { 'X-Locale': apiLocale },
        });

    const put = async (payload: TPhoneVerificationPUT): Promise<AxiosResponse<TPhoneVerificationResponse>> =>
        api.put<TPhoneVerificationResponse>('/_svc/customer-v1/customer/phone/verify', payload, {
            headers: { 'X-Locale': apiLocale },
        });

    const requestCallback = async (phoneNr: string): Promise<AxiosResponse<TPhonesCallbackResponse>> =>
        api.post<TPhonesCallbackResponse>(
            '/_svc/customer-v1/customer/phone-callback',
            { phone: phoneNr },
            { headers: { 'X-Locale': apiLocale } },
        );

    return {
        get,
        post,
        put,
        requestCallback,
    };
};

// ----------------------------------------- PROFILE -----------------------------------------
type TProfileEndpoints = {
    get: () => Promise<AxiosResponse<TProfile>>;
    put: (userProfile: TProfilePUT) => Promise<AxiosResponse<TProfile>>;
    updatePassword: (credentials: TPasswordPUT) => Promise<AxiosResponse<TProfile>>;
};

const profile = (api: AxiosInstance, apiLocale: TLocaleAPI): TProfileEndpoints => {
    const get = async (): Promise<AxiosResponse<TProfile>> =>
        api.get<TProfile>('/_svc/customer-v1/customer', { headers: { 'X-Locale': apiLocale } });
    const put = async (userProfile: TProfilePUT): Promise<AxiosResponse<TProfile>> =>
        api.put<TProfile>('/_svc/customer-v1/customer', userProfile, { headers: { 'X-Locale': apiLocale } });
    const updatePassword = async (credentials: TPasswordPUT): Promise<AxiosResponse<TProfile>> =>
        api.put<TProfile>('/_svc/customer-v1/customer', credentials, { headers: { 'X-Locale': apiLocale } });

    return {
        get,
        put,
        updatePassword,
    };
};

// ----------------------------------------- CREDIT -----------------------------------------
type TCreditsEndpoints = {
    get: (prevToken: string | null, nextToken: string | null) => Promise<AxiosResponse<TCredit>>;
    redeem: (code: string) => Promise<AxiosResponse<TRedeemResponse>>;
};

const credits = (api: AxiosInstance, apiLocale: TLocaleAPI): TCreditsEndpoints => {
    const get = async (prevToken: string | null, nextToken: string | null): Promise<AxiosResponse<TCredit>> => {
        let qString = '';
        if (prevToken && nextToken) {
            qString = `?prevToken=${prevToken}&nextToken=${nextToken}`;
        } else if (prevToken) {
            qString = `?prevToken=${prevToken}`;
        } else if (nextToken) {
            qString = `?nextToken=${nextToken}`;
        }

        return api.get<TCredit>(`/_svc/customer-v1/credit${qString}`, { headers: { 'X-Locale': apiLocale } });
    };
    const redeem = async (code: string): Promise<AxiosResponse<TRedeemResponse>> =>
        api.post<TRedeemResponse>('/_svc/customer-v1/gift-card', { code }, { headers: { 'X-Locale': apiLocale } });

    return {
        get,
        redeem,
    };
};

// ----------------------------------------- CREDIT -----------------------------------------
type TWalletEndpoints = {
    getTransfers: () => Promise<AxiosResponse<{ data: TGetTransferData }>>;
    getAccounts: () => Promise<AxiosResponse<{ data: TBankAccount[] }>>;
    postAccount: (data: TBankAccount) => Promise<AxiosResponse<TBankAccountReponse>>;
    postBankTransfer: (data: TBankTransfer) => Promise<AxiosResponse<TTransfersResponse>>;
    otpRequest: (data: TBankTransferOTPPayload) => Promise<AxiosResponse<TRequestBankTransferOTPResponse>>;
    otpSubmit: (data: TBankTransferOTPSubmitPayload) => Promise<AxiosResponse<TBankTransferOTPSubmitResponse>>;
};

const wallet = (api: AxiosInstance, apiLocale: TLocaleAPI): TWalletEndpoints => {
    const getTransfers = async (): Promise<AxiosResponse<{ data: TGetTransferData }>> =>
        api.get<{ data: TGetTransferData }>('/_svc/customer-v1/bank-transfer', { headers: { 'X-Locale': apiLocale } });

    const getAccounts = async (): Promise<AxiosResponse<{ data: TBankAccount[] }>> =>
        api.get<{ data: TBankAccount[] }>('/_svc/customer-v1/customer/bank-account', {
            headers: { 'X-Locale': apiLocale },
        });

    const postAccount = async (data: TBankAccount): Promise<AxiosResponse<TBankAccountReponse>> =>
        api.post<TBankAccountReponse>('/_svc/customer-v1/customer/bank-account', data, {
            headers: { 'X-Locale': apiLocale },
        });

    const postBankTransfer = async (data: TBankTransfer): Promise<AxiosResponse<TTransfersResponse>> =>
        api.post<TTransfersResponse>('/_svc/customer-v1/bank-transfer', data, { headers: { 'X-Locale': apiLocale } });

    const otpRequest = async (data: TBankTransferOTPPayload): Promise<AxiosResponse<TRequestBankTransferOTPResponse>> =>
        api.post<TRequestBankTransferOTPResponse>('/_svc/customer-v1/bank-transfer/otp', data, {
            headers: { 'X-Locale': apiLocale },
        });

    const otpSubmit = async (
        data: TBankTransferOTPSubmitPayload,
    ): Promise<AxiosResponse<TBankTransferOTPSubmitResponse>> =>
        api.post<TBankTransferOTPSubmitResponse>('/_svc/customer-v1/verify/bank-transfer/otp', data, {
            headers: { 'X-Locale': apiLocale },
        });

    return {
        getTransfers,
        getAccounts,
        postAccount,
        postBankTransfer,
        otpRequest,
        otpSubmit,
    };
};
// ----------------------------------------- RETURNS -----------------------------------------
type TReturnsEndpoints = {
    get: (returnNr?: string) => Promise<AxiosResponse<TReturns>>;
    post: (returns: TReturnsPOST) => Promise<AxiosResponse<TReturnsPOSTResponse>>;
    getReturnable: (page: number) => Promise<AxiosResponse<TReturnables>>;
    track: (returnNr: string) => Promise<AxiosResponse<TReturnsTrackingResponse>>;
    postChangeAddress: (
        returnNr: string,
        addressData: TReturnChangeAddressPOST,
    ) => Promise<AxiosResponse<TReturnChangeAddressPOSTResponse>>;
};

const returns = (api: AxiosInstance, apiLocale: TLocaleAPI): TReturnsEndpoints => {
    const get = async (returnNr?: string): Promise<AxiosResponse<TReturns>> =>
        api.get<TReturns>(`/_svc/customer-v1/customer/returns${returnNr ? `?returnNr=${returnNr}` : ''}`, {
            headers: { 'X-Locale': apiLocale },
        });

    const post = async (returnItems: TReturnsPOST): Promise<AxiosResponse<TReturnsPOSTResponse>> =>
        api.post<TReturnsPOSTResponse>('/_svc/customer-v1/customer/returns', returnItems, {
            headers: { 'X-Locale': apiLocale },
        });

    const getReturnable = async (page = 1): Promise<AxiosResponse<TReturnables>> =>
        api.get<TReturnables>(`/_svc/customer-v1/customer/returnable?page=${page}`, {
            headers: { 'X-Locale': apiLocale },
        });

    const track = async (returnNr: string): Promise<AxiosResponse<TReturnsTrackingResponse>> => {
        return api.get(`/_svc/customer-v1/customer/returns/track/${returnNr}`, { headers: { 'X-Locale': apiLocale } });
    };
    const postChangeAddress = async (
        returnNr: string,
        addressData: TReturnChangeAddressPOST,
    ): Promise<AxiosResponse<TReturnChangeAddressPOSTResponse>> =>
        api.post<TReturnChangeAddressPOSTResponse>(
            `/_svc/customer-v1/customer/return-request/${returnNr}/address`,
            addressData,
            { headers: { 'X-Locale': apiLocale } },
        );

    return {
        get,
        post,
        getReturnable,
        track,
        postChangeAddress,
    };
};

// ----------------------------------------- PAYMENT -----------------------------------------
type TPaymentEndpoints = {
    get: () => Promise<AxiosResponse<TPaymentCardsResponse>>;
    defaultCard: (cardId: string) => Promise<AxiosResponse<TPaymentCardsResponse>>;
    remove: (cardId: string) => Promise<AxiosResponse<TPaymentCardsResponse>>;
    post: (cardDetails: any) => Promise<AxiosResponse<TPaymentCardsResponse>>;
    initTokens: () => Promise<AxiosResponse<TInitTokens>>;
    tokens: (payload: TTokensPOST) => Promise<AxiosResponse<TTokensPOSTResponse>>;
};

const payment = (api: AxiosInstance, apiLocale: TLocaleAPI): TPaymentEndpoints => {
    const get = async (): Promise<AxiosResponse<TPaymentCardsResponse>> =>
        api.get<TPaymentCardsResponse>('/_svc/customer-v1/customer/paymentcards', {
            headers: { 'X-Locale': apiLocale },
        });

    const defaultCard = async (cardId: string): Promise<AxiosResponse<TPaymentCardsResponse>> =>
        api.post<TPaymentCardsResponse>(
            `/_svc/customer-v1/customer/paymentcards/${cardId}/default`,
            {},
            {
                headers: { 'X-Locale': apiLocale },
            },
        );

    const post = async (cardDetails: any) =>
        api.post('/_svc/customer-v1/customer/tokens', cardDetails, {
            headers: { 'X-Locale': apiLocale },
        });

    const remove = async (cardId: string): Promise<AxiosResponse<TPaymentCardsResponse>> =>
        api.delete<TPaymentCardsResponse>(`/_svc/customer-v1/customer/paymentcards/${cardId}?fetch=full`, {
            headers: { 'X-Locale': apiLocale },
        });

    const initTokens = async (): Promise<AxiosResponse<TInitTokens>> =>
        api.get<TInitTokens>('/_svc/customer-v1/customer/tokens/init', { headers: { 'X-Locale': apiLocale } });

    const tokens = async (payload: TTokensPOST): Promise<AxiosResponse<TTokensPOSTResponse>> =>
        api.post<TTokensPOSTResponse>('/_svc/customer-v1/customer/tokens', payload, {
            headers: { 'X-Locale': apiLocale },
        });

    return {
        get,
        defaultCard,
        post,
        remove,
        initTokens,
        tokens,
    };
};

// ----------------------------------------- CLAIMS -----------------------------------------
type TClaimsEndpoints = {
    get: () => Promise<AxiosResponse<TClaimsGETResponse>>;
    post: (claim: TClaimsPOST) => Promise<AxiosResponse<any>>;
    getClaimables: () => Promise<AxiosResponse<TClaimablesGETResponse>>;
    postChangeAddress: (
        returnNr: string,
        addressData: TClaimChangeAddressPOST,
    ) => Promise<AxiosResponse<TReturnChangeAddressPOSTResponse>>;
};

const claims = (api: AxiosInstance, apiLocale: TLocaleAPI): TClaimsEndpoints => {
    const get = async (): Promise<AxiosResponse<TClaimsGETResponse>> =>
        api.get<TClaimsGETResponse>('/_svc/customer-v1/customer/claim-requests', {
            headers: { 'X-Locale': apiLocale },
        });

    const post = async (claim: TClaimsPOST): Promise<AxiosResponse<any>> =>
        api.post<any>('/_svc/customer-v1/customer/claim', claim, { headers: { 'X-Locale': apiLocale } });

    const getClaimables = async (): Promise<AxiosResponse<TClaimablesGETResponse>> =>
        api.get<TClaimablesGETResponse>('/_svc/customer-v1/customer/claimable', { headers: { 'X-Locale': apiLocale } });

    const postChangeAddress = async (
        claimNr: string,
        addressData: TClaimChangeAddressPOST,
    ): Promise<AxiosResponse<TReturnChangeAddressPOSTResponse>> =>
        api.post<TReturnChangeAddressPOSTResponse>(`/_svc/customer-v1/customer/claim/${claimNr}/address`, addressData, {
            headers: { 'X-Locale': apiLocale },
        });

    return {
        get,
        post,
        getClaimables,
        postChangeAddress,
    };
};

// ----------------------------------------- OTP -----------------------------------------
type TPhoneVerificationEndpoints = {
    request: (payload: TRequestOTPPayload) => Promise<AxiosResponse<TRequestOTPResponse>>;
    submit: (payload: TSubmitOTPPayload) => Promise<AxiosResponse<TSubmitOTPResponse>>;
};

const otp = (api: AxiosInstance, apiLocale: TLocaleAPI): TPhoneVerificationEndpoints => {
    const request = async (payload: TRequestOTPPayload): Promise<AxiosResponse<TRequestOTPResponse>> =>
        api.post<TRequestOTPResponse>('/_svc/customer-v1/customer/phone/verify', payload, {
            headers: { 'X-Locale': apiLocale },
        });
    const submit = async (payload: TSubmitOTPPayload): Promise<AxiosResponse<TSubmitOTPResponse>> =>
        api.put<TSubmitOTPResponse>('/_svc/customer-v1/customer/phone/verify', payload, {
            headers: { 'X-Locale': apiLocale },
        });

    return {
        request,
        submit,
    };
};
// ----------------------------------------- LOYALTY -----------------------------------------
type TLoyaltyEndpoints = {
    get: () => Promise<AxiosResponse<TGetLoyaltyResponse>>;
};

const loyalty = (api: AxiosInstance, apiLocale: TLocaleAPI): TLoyaltyEndpoints => {
    const get = async (): Promise<AxiosResponse<TGetLoyaltyResponse>> =>
        api.get<TGetLoyaltyResponse>('/_svc/customer-v1/customer/loyalty', { headers: { 'X-Locale': apiLocale } });

    return {
        get,
    };
};

// ---------------------------PREFERENCES----------------------------------
type TPreferencesEndPoints = {
    get: () => Promise<AxiosResponse<TPreferencesGETResponse>>;
    post: (postData: TPreferencePOSTRequest) => Promise<AxiosResponse<TPreferencesGETResponse>>;
};

const preferences = (api: AxiosInstance, apiLocale: TLocaleAPI): TPreferencesEndPoints => {
    const get = async (): Promise<AxiosResponse<TPreferencesGETResponse>> =>
        api.get<TPreferencesGETResponse>('/_svc/customerpref-v1/preferences', { headers: { 'X-Locale': apiLocale } });

    const post = async (postData: TPreferencePOSTRequest): Promise<AxiosResponse<TPreferencesGETResponse>> =>
        api.post<TPreferencesGETResponse>('/_svc/customerpref-v1/preferences', postData, {
            headers: { 'X-Locale': apiLocale },
        });

    return {
        get,
        post,
    };
};

export type TCustomerEndpoints = {
    address: TAddressesEndpoints;
    order: TOrdersEndpoints;
    user: TUserAuthenticationEndpoints;
    homat: THomatEndpoints;
    phone: TPhoneEndpoints;
    profile: TProfileEndpoints;
    credit: TCreditsEndpoints;
    wallet: TWalletEndpoints;
    return: TReturnsEndpoints;
    payment: TPaymentEndpoints;
    claim: TClaimsEndpoints;
    otp: TPhoneVerificationEndpoints;
    loyalty: TLoyaltyEndpoints;
    preferences: TPreferencesEndPoints;
};

export default (api: AxiosInstance, apiLocale: TLocaleAPI): TCustomerEndpoints => {
    return {
        address: address(api, apiLocale),
        order: order(api, apiLocale),
        user: user(api, apiLocale),
        homat: homat(api, apiLocale),
        phone: phone(api, apiLocale),
        profile: profile(api, apiLocale),
        credit: credits(api, apiLocale),
        wallet: wallet(api, apiLocale),
        return: returns(api, apiLocale),
        payment: payment(api, apiLocale),
        claim: claims(api, apiLocale),
        otp: otp(api, apiLocale),
        loyalty: loyalty(api, apiLocale),
        preferences: preferences(api, apiLocale),
    };
};
